/** One campaign: its totals, its shape, and every link inside it. */
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking } from '../../lib/urltracking.js';
import { formatDate, parseDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group } from '../Sheet.jsx';
import { ClicksChart, Breakdown, TrackStatus, ShortUrl, Recipients, clickStamp, num } from '../../pages/urltracking/TrackBits.jsx';

export default function TrackCampaignView({ id, onClose }) {
  const { S, setSheet, can } = useApp();
  const { data, error, loading } = useTracking(() => api.urlTracking.analytics.campaign(id), [id]);
  const recipients = useTracking(() => api.urlTracking.analytics.recipients({ campaignId: id }), [id]);

  const campaign = data?.campaign;
  const t = data?.totals || {};

  return (
    <Sheet
      icon="mega"
      tone="g-pink"
      title={campaign?.name || 'Campaign'}
      badge={campaign ? <TrackStatus value={campaign.status} /> : null}
      onClose={onClose}
      closeLabel="Close"
      wide
      footerLeft={campaign && can('utm-campaigns', 'edit') ? (
        <button type="button" className="btn" onClick={() => setSheet({ kind: 'trackCampaign', id })}>
          <Icon name="edit" />Edit campaign
        </button>
      ) : null}
    >
      {error ? <p className="utbad">{error}</p> : null}
      {loading && !data ? <p className="muted">Loading…</p> : null}

      {campaign ? (
        <>
          {campaign.description ? <p className="utnote">{campaign.description}</p> : null}

          <div className="utstats">
            {[
              ['Tracking URLs', num(data.links?.length)],
              ['Total clicks', num(t.clicks)],
              ['Unique visitors (est.)', num(t.uniqueVisitors)],
              ['Today', num(t.clicksToday)],
              ['This month', num(t.clicksThisMonth)]
            ].map(([label, value]) => (
              <div key={label}><b>{value}</b><span>{label}</span></div>
            ))}
          </div>

          <Group>Clicks over time</Group>
          <div className="f wide"><ClicksChart series={data.series} height={160} /></div>

          <Group>Breakdown</Group>
          <div className="f wide utbreak">
            <Breakdown title="Source" icon="target" tone="g-blue" rows={data.sources} />
            <Breakdown title="Medium" icon="flow" tone="g-pink" rows={data.mediums} />
            <Breakdown title="Content" icon="file" tone="g-orange" rows={data.contents} />
            <Breakdown title="Device" icon="grid" tone="g-cyan" rows={data.devices} />
          </div>

          <Group>Tracking URLs in this campaign</Group>
          <div className="f wide tscroll">
            {data.links?.length ? (
              <table>
                <thead>
                  <tr><th>Name</th><th>Source</th><th>Content</th><th>Short URL</th><th>Clicks</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {data.links.map(l => (
                    <tr key={l.id} onClick={() => setSheet({ kind: 'trackLinkView', id: l.id })}>
                      <td><b>{l.name || 'Untitled'}</b></td>
                      <td className="dim">{l.utm_source || '—'}</td>
                      <td className="dim">{l.utm_content || '—'}</td>
                      <td onClick={e => e.stopPropagation()}><ShortUrl url={l.short_url} compact /></td>
                      <td><b>{num(l.clicks)}</b></td>
                      <td><TrackStatus value={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="muted">No tracking URLs in this campaign yet.</p>}
          </div>

          <Group>Recipients</Group>
          <div className="f wide">
            <Recipients
              data={recipients.data}
              empty="No personalised links in this campaign. Open one of its tracking URLs and generate one per recipient to see who clicked."
            />
          </div>

          <Group>Details</Group>
          <div className="f wide">
            <dl className="utdl">
              <dt>Status</dt><dd>{campaign.status}</dd>
              <dt>Runs</dt>
              <dd>
                {campaign.start_date || campaign.end_date
                  ? `${day(campaign.start_date, S.dateFmt)} → ${day(campaign.end_date, S.dateFmt)}`
                  : 'No dates set'}
              </dd>
              <dt>First click</dt><dd>{t.firstClick ? clickStamp(t.firstClick, S.tz) : 'Never clicked'}</dd>
              <dt>Last click</dt><dd>{t.lastClick ? clickStamp(t.lastClick, S.tz) : '—'}</dd>
              {campaign.notes ? <><dt>Notes</dt><dd>{campaign.notes}</dd></> : null}
            </dl>
          </div>
        </>
      ) : null}
    </Sheet>
  );
}

function day(value, fmt, withTime) {
  const d = parseDate(value);
  return d ? formatDate(d, !!withTime, fmt) : '…';
}
